import { ProductPriceAdvantage, ProductRankingFilters } from './types';
import { calculateProductPriceAdvantages } from './productRanking';

export async function calculateDistributionCenterRankings(
  filters: Omit<ProductRankingFilters, 'marketGroup'>
): Promise<ProductPriceAdvantage[]> {
  return calculateProductPriceAdvantages({
    ...filters,
    marketGroup: 'distribution-center',
  });
}

export async function calculateSupermarketRankings(
  filters: Omit<ProductRankingFilters, 'marketGroup'>
): Promise<ProductPriceAdvantage[]> {
  return calculateProductPriceAdvantages({
    ...filters,
    marketGroup: 'supermarket',
  });
}

export async function calculateOverallMarketRankings(
  filters: Omit<ProductRankingFilters, 'marketGroup'>
): Promise<ProductPriceAdvantage[]> {
  // Calculate rankings across all market groups and average them
  const marketGroups = ['distribution-center', 'supermarket', 'local-shops', 'sunday-market', 'ecommerce'];
  
  const allRankings = await Promise.all(
    marketGroups.map(marketGroup =>
      calculateProductPriceAdvantages({
        ...filters,
        marketGroup,
      }).catch(() => [] as ProductPriceAdvantage[])
    )
  );

  // Aggregate results by product
  const productMap = new Map<string, ProductPriceAdvantage[]>();
  
  allRankings.flat().forEach(ranking => {
    if (!productMap.has(ranking.productName)) {
      productMap.set(ranking.productName, []);
    }
    productMap.get(ranking.productName)!.push(ranking);
  });

  // Calculate overall averages
  const overallRankings: ProductPriceAdvantage[] = [];
  
  productMap.forEach((rankings, productName) => {
    const avgDiscountPercentage = rankings.reduce((sum, r) => sum + r.avgDiscountPercentage, 0) / rankings.length;
    const avgChipchipPrice = rankings.reduce((sum, r) => sum + r.avgChipchipPrice, 0) / rankings.length;
    const avgMarketPrice = rankings.reduce((sum, r) => sum + r.avgMarketPrice, 0) / rankings.length;
    const avgAbsoluteSavings = rankings.reduce((sum, r) => sum + r.avgAbsoluteSavings, 0) / rankings.length;
    const avgConsistency = rankings.reduce((sum, r) => sum + r.consistencyScore, 0) / rankings.length;
    
    const totalDays = Math.max(...rankings.map(r => r.totalDays));
    const totalDaysCheaper = rankings.reduce((sum, r) => sum + r.daysChipchipCheaper, 0);
    
    overallRankings.push({
      productName,
      marketGroup: 'Overall Market',
      avgDiscountPercentage,
      maxDiscountPercentage: Math.min(...rankings.map(r => r.maxDiscountPercentage)),
      minDiscountPercentage: Math.max(...rankings.map(r => r.minDiscountPercentage)),
      daysChipchipCheaper: totalDaysCheaper,
      totalDays: totalDays * rankings.length,
      percentDaysCheaper: (totalDaysCheaper / (totalDays * rankings.length)) * 100,
      avgChipchipPrice,
      avgMarketPrice,
      avgAbsoluteSavings,
      consistencyScore: avgConsistency,
    });
  });

  return overallRankings.sort((a, b) => a.avgDiscountPercentage - b.avgDiscountPercentage);
}

export async function calculateConsistencyRankings(
  filters: Omit<ProductRankingFilters, 'marketGroup'>
): Promise<ProductPriceAdvantage[]> {
  const marketGroup = 'distribution-center';
  const rankings = await calculateProductPriceAdvantages({
    ...filters,
    marketGroup,
  });

  return rankings.sort((a, b) => b.consistencyScore - a.consistencyScore);
}

export async function calculateVolumeOpportunityRankings(
  filters: Omit<ProductRankingFilters, 'marketGroup'>
): Promise<ProductPriceAdvantage[]> {
  const marketGroup = 'distribution-center';
  const rankings = await calculateProductPriceAdvantages({
    ...filters,
    marketGroup,
  });

  // Calculate opportunity score (higher discount % + more days cheaper = better opportunity)
  const rankedWithOpportunity = rankings.map(ranking => ({
    ...ranking,
    opportunityScore: Math.abs(ranking.avgDiscountPercentage) * ranking.percentDaysCheaper * 
                      (ranking.consistencyScore / 100),
  }));

  return rankedWithOpportunity.sort((a, b) => b.opportunityScore - a.opportunityScore);
}
